// shared/src/oracle/relations-config-store.ts
// D1 access for `oracle_relations_config` (migration 0111) — the wrapped<->native peg RELATIONS
// as EDITABLE config, replacing the hardcoded TOKEN_RELATIONS constant as the live source of truth
// (#93 relation-CRUD). The producer reads enabled rows each fire and maps them to TokenRelation;
// the api-worker admin route upserts/deletes rows (add/edit/retire from the /oracle Relations tab).
//
// Row/Zod/reader/writer live together and ship with the DDL in ONE commit (feedback_writer_ddl_lockstep).
// checkRelation (token-relations.ts) is unchanged — it consumes the TokenRelation this store produces.
// Uses the shared OracleD1 statement surface so the admin route can batch the config-history rows
// (oracleConfigHistoryInsertStmts) atomically with the upsert/delete.

import { z } from 'zod';
import type { TokenRelation } from './token-relations.js';
import type { OracleD1, OracleD1Stmt } from './oracle-store.js';

export interface OracleRelationConfigRow {
  readonly key:            string;   // wrapped key ('$WBTC')
  readonly native_key:     string;   // native reference ('$BTC')
  readonly ratio:          number;   // fixed expected ratio (key ~= native x ratio)
  readonly tol:            number;   // fractional deviation that FLAGS a breach
  readonly allow_fallback: number;   // 0/1 — SQLite has no bool
  readonly enabled:        number;   // 0/1 — 0 = retired (row kept), 1 = live
  readonly note:           string | null;
  readonly updated_at:     string;   // ISO-Z
  readonly updated_by:     string;
}

export const OracleRelationConfigRowSchema = z.object({
  key:            z.string().min(1),
  native_key:     z.string().min(1),
  ratio:          z.number().finite().positive(),
  tol:            z.number().finite().positive(),
  allow_fallback: z.number().int().min(0).max(1),
  enabled:        z.number().int().min(0).max(1),
  note:           z.string().nullable(),
  updated_at:     z.string().min(1),
  updated_by:     z.string().min(1),
}).refine((r) => r.key !== r.native_key, {
  message: 'a relation cannot reference itself (key === native_key)',
});

/** A config row -> the TokenRelation the resolver/producer consume. Every live relation is a
 *  FIXED-ratio wrapper (the 'onchain' accruing variant was retired with $WSTETH). */
export function toTokenRelation(row: OracleRelationConfigRow): TokenRelation {
  return {
    key:           row.key,
    nativeKey:     row.native_key,
    ratio:         { kind: 'fixed', ratio: row.ratio },
    tol:           row.tol,
    allowFallback: row.allow_fallback === 1,
  };
}

const COLS = 'key, native_key, ratio, tol, allow_fallback, enabled, note, updated_at, updated_by';

/** ALL relation config rows (enabled + retired), live first then by key, for the dashboard. */
export async function readRelationsConfig(db: OracleD1): Promise<OracleRelationConfigRow[]> {
  const res = await db
    .prepare(`SELECT ${COLS} FROM oracle_relations_config ORDER BY enabled DESC, key ASC`)
    .all<OracleRelationConfigRow>();
  return res.results ?? [];
}

/** The CURRENTLY-WATCHED relations as TokenRelation[] — enabled rows only. This is what the
 *  producer loops each fire; the breach-log filter keys on these keys so a retired/absent
 *  relation drops out and its stale rows never resurface. */
export async function readEnabledRelations(db: OracleD1): Promise<TokenRelation[]> {
  const res = await db
    .prepare(`SELECT ${COLS} FROM oracle_relations_config WHERE enabled = 1 ORDER BY key ASC`)
    .all<OracleRelationConfigRow>();
  return (res.results ?? []).map(toTokenRelation);
}

/** Idempotent upsert of one relation config row. Extra statements (e.g. the config-history rows
 *  the admin route builds via oracleConfigHistoryInsertStmts) commit in the SAME db.batch so the
 *  audit trail can never land without the row it describes (mirrors upsertSource/upsertPolicy). */
export async function upsertRelationConfig(
  db: OracleD1, row: OracleRelationConfigRow, extraStmts: OracleD1Stmt[] = [],
): Promise<void> {
  const upsert = db
    .prepare(
      `INSERT INTO oracle_relations_config (${COLS})
       VALUES (?,?,?,?,?,?,?,?,?)
       ON CONFLICT(key) DO UPDATE SET
         native_key=excluded.native_key, ratio=excluded.ratio, tol=excluded.tol,
         allow_fallback=excluded.allow_fallback, enabled=excluded.enabled, note=excluded.note,
         updated_at=excluded.updated_at, updated_by=excluded.updated_by`,
    )
    .bind(
      row.key, row.native_key, row.ratio, row.tol, row.allow_fallback,
      row.enabled, row.note, row.updated_at, row.updated_by,
    );
  await db.batch([upsert, ...extraStmts]);
}

/** Hard-delete a relation config row by key (with optional batched history statements). Retiring a
 *  relation usually sets enabled=0 to keep its drift history readable; delete is for a mistaken add. */
export async function deleteRelationConfig(
  db: OracleD1, key: string, extraStmts: OracleD1Stmt[] = [],
): Promise<void> {
  const del = db.prepare('DELETE FROM oracle_relations_config WHERE key = ?').bind(key);
  await db.batch([del, ...extraStmts]);
}
